import type { RetailVerificationMethod } from "@solomon-apps/shared";

export type WalletVerificationErrorCode =
    | "wallet_rejected"
    | "wallet_mismatch"
    | "unsupported_signing"
    | "signing_failed"
    | "signing_timeout"
    | "nonce_expired"
    | "transfer_failed"
    | "transfer_unavailable";

export class WalletVerificationError extends Error {
    public readonly status = null;
    public readonly requestId = null;
    public readonly retryAfterSeconds = null;

    constructor(
        message: string,
        public readonly code: WalletVerificationErrorCode,
        public readonly method: RetailVerificationMethod | null = null
    ) {
        super(message);
        this.name = "WalletVerificationError";
    }
}

/** ConnectorKit wraps provider errors in originalError; other adapters use cause. */
export function classifyWalletSigningError(error: unknown): WalletVerificationErrorCode {
    const pending: unknown[] = [error];
    const seen = new Set<unknown>();
    let rejected = false;
    let unsupported = false;
    while (pending.length > 0 && seen.size < 16) {
        const value = pending.shift();
        if (!value || typeof value !== "object" || seen.has(value)) continue;
        seen.add(value);
        const message = "message" in value && typeof value.message === "string" ? value.message : "";
        const code = "code" in value ? value.code : undefined;
        if (
            code === "FEATURE_NOT_SUPPORTED" ||
            code === 4200 ||
            /not supported|unsupported|does not support|doesn't support/i.test(message)
        ) {
            unsupported = true;
        }
        if (
            code === 4001 ||
            code === "4001" ||
            code === "USER_REJECTED" ||
            /user (rejected|denied|cancelled|canceled|disapproved)|request (cancelled|canceled)|cancelled by user|canceled by user/i.test(
                message
            )
        ) {
            rejected = true;
        }
        if ("originalError" in value) pending.push(value.originalError);
        if ("cause" in value) pending.push(value.cause);
    }
    if (rejected) return "wallet_rejected";
    return unsupported ? "unsupported_signing" : "signing_failed";
}
